import { unparse } from "papaparse";
import moment from "moment";

// store
import useAuthStore from "store/useAuthStore";

// types
import { Generic } from "types/filters-store.types";
import { BusinessItem } from "types/results-panel.types";

// helper
import {
  getCapabilitiesNarrativeLink,
  refineCertificationTags,
  formatUnixTimestamp,
  getLegalStructureName
} from "./helper";
import { Permissions } from "constants/permissions";

export const DefaultExportColumns = [
  {
    id: "legal_business_name",
    name: "legal_business_name",
    label: "Business name (Required)",
    labelDescription: "",
  },
  {
    id: "capabilities_narrative",
    name: "capabilities_narrative",
    label: "Capabilities narrative and capabilities statement link",
    labelDescription:
      "In a spreadsheet export, the capabilities narrative and capabilities statement will be separated into 2 columns.",
  },
  {
    id: "certs",
    name: "certs",
    label: "Active SBA certifications",
    labelDescription:
      "This column will list each active SBA certification in a single cell. To separate them, scroll to the SBA certification settings section below.",
  },
  {
    id: "contact_person",
    name: "contact_person",
    label: "Contact person's name and email address",
    labelDescription:
      "In a spreadsheet export, the contact person's name and their email address will be separated into 2 columns.",
  },
  {
    id: "address_1",
    name: "address_1",
    label: "Address",
    labelDescription:
      "In a spreadsheet export, each part of the business address will be separated into columns: street address line 1, street address line 2, city, state, and zip code.",
  },
];

export const ExportColumns = [
  {
    name: "Overview information",
    visible: true,
    columns: [
      {
        id: "Unique Entity Identifier",
        name: "uei",
        label: "UEI (Unique Entity Identifier)",
      },
      {
        id: "Last updated date",
        name: "last_update_date",
        label: "Last updated date",
      },
      {
        id: "Phone number",
        name: "phone",
        label: "Phone number",
      },
      {
        id: "Fax number",
        name: "fax",
        label: "Fax number",
      },
      {
        id: "website",
        name: "website",
        label: "Website",
      },
      {
        id: "additional_website",
        name: "additional_website",
        label: "Additional website",
      },
      {
        id: "dba_name",
        name: "dba_name",
        label: "Business trade name",
      },
      {
        id: "county",
        name: "county",
        label: "County name",
      },
      {
        id: "county-code",
        name: "county_code",
        label: "County code",
      },
      {
        id: "congressional-district",
        name: "congressional_district",
        label: "Congressional district",
      },
      {
        id: "msa",
        name: "msa",
        label: "Metropolitan statistical area",
      },
      {
        id: "CAGE-code",
        name: "cage_code",
        label: "CAGE code",
      },
      {
        id: "year-established",
        name: "year_established",
        label: "Year established",
      },
      // {
      //   id: "accepts-government",
      //   name: "government_credit_card",
      //   label: "Accepts government credit card?",
      // },
    ],
  },
  {
    name: "Organization & ownership",
    visible: true,
    columns: [
      {
        id: "legal-structure",
        name: "legal_structure",
        label: "Legal structure",
      },
      {
        id: "current-principal",
        name: "current_principals",
        label: "Current principals",
        labelDescription:
          "Principal names and titles will be in a numbered list in a single cell.",
      },
      {
        id: "meili_self_certifications",
        name: "meili_self_certifications",
        label: "Business type and self-certifications",
        labelDescription:
          "In a spreadsheet export, list items will be comma-separated in a single cell.",
      },
    ],
  },
  {
    name: "Size",
    visible: false,
    columns: [
      {
        id: "business-size",
        name: "business_size",
        label: "Number of employees",
      },
      {
        id: "annual-revenue",
        name: "annual_revenue",
        label: "Annual gross revenue",
      },
    ],
  },
  {
    name: "Certifications",
    visible: true,
    subColumns: [
      {
        name: "8(a)",
        columns: [
          {
            id: "certification-status",
            name: "certStatus_8a",
            label: "8(a) certification status",
            labelDescription:
              "The cell will say active, suspended, previously certified, or will be left blank.",
          },
          {
            id: "entrance-date",
            name: "certDateStart_8a",
            label: "8(a) entrance date",
          },
          {
            id: "exit-date",
            name: "certDateExit_8a",
            label: "8(a) exit date",
          },
          {
            id: "joint-venture-certification",
            name: "certStatus_8aJV",
            label: "8(a) Joint Venture certification status",
            labelDescription:
              "The cell will say active, previously certified, or will be left blank.",
          },
          {
            id: "joint-venture-entrance-date",
            name: "certDateStart_8aJV",
            label: "8(a) Joint Venture entrance date",
          },
          {
            id: "joint-venture-exit-date",
            name: "certDateExit_8aJV",
            label: "8(a) Joint Venture exit dates",
          },
        ],
      },
      {
        name: "HUBZone",
        columns: [
          {
            id: "HBZone-status",
            name: "certStatus_HZ",
            label: "HUBZone certification status",
            labelDescription:
              "The cell will say active, previously certified, or will be left blank.",
          },
          {
            id: "HUBZone-date",
            name: "certDateStart_HZ",
            label: "HUBZone certification date",
          },
        ],
      },
      {
        name: "Women-Owned Small Business",
        columns: [
          {
            id: "certStatus_WOSB",
            name: "certStatus_WOSB",
            label: "Women-Owned Small Business (WOSB) certification status",
            labelDescription:
              "The cell will say pending, active, previously certified, or will be left blank.",
          },
          {
            id: "WOSB-date",
            name: "certDateStart_WOSB",
            label: "Women-Owned Small Business (WOSB) certification date",
          },
          {
            id: "certStatus_EDWOSB",
            name: "certStatus_EDWOSB",
            label:
              "Economically-Disadvantaged Women-Owned Small Business (EDWOSB) certification status",
            labelDescription:
              "The cell will say pending, active, previously certified, or will be left blank.",
          },
          {
            id: "EDWOSB-date",
            name: "certDateStart_EDWOSB",
            label:
              "Economically-Disadvantaged Women-Owned Small Business (EDWOSB) certification date",
          },
        ],
      },
      {
        name: "Veteran-Owned Small Business",
        columns: [
          {
            id: "certStatus_VOSB",
            name: "certStatus_VOSB",
            label: "Veteran-Owned Small Business (VOSB) certification status",
            labelDescription:
              "The cell will say active, previously certified, or will be left blank.",
          },

          // {
          //   id: "VOSB-joint-status",
          //   name: "certStatus_VOSBJV",
          //   label:
          //     "Veteran-Owned Small Business Joint Venture (VOSB JV) certification status",
          //   labelDescription:
          //     "The cell will say active, previously certified, or will be left blank.",
          // },

          {
            id: "VOSB-entrance-date",
            name: "certDateStart_VOSB",
            label: "Veteran-Owned Small Business (VOSB) entrance date",
          },

          {
            id: "VOSB-exit-date",
            name: "certDateExit_VOSB",
            label: "Veteran-Owned Small Business (VOSB) exit dates",
          },

          {
            id: "SDVOSB-status",
            name: "certStatus_SDVOSB",
            label:
              "Service-Disabled Veteran-Owned Small Business (SDVOSB) certification status",
            labelDescription:
              "The cell will say active, expired, or will be left blank.",
          },

          {
            id: "SDVOSB-entrance-date",
            name: "certDateStart_SDVOSB",
            label:
              "Service-Disabled Veteran-Owned Small Business (SDVOSB) entrance date",
          },

          {
            id: "SDVOSB-exit-date",
            name: "certDateExit_SDVOSB",
            label: "Service-Disabled Veteran-Owned Small Business (SDVOSB) exit date",
          },

          // {
          //   id: "SDVOSB-Joint-status",
          //   name: "certStatus_SDVOSBJV",
          //   label:
          //     "Service-Disabled Veteran-Owned Small Business Joint Venture (SDVOSB JV) certification status",
          //   labelDescription:
          //     "The cell will say active, expired, or will be left blank.",
          // },

          {
            id: "non_fed_govt_certifications",
            name: "non_fed_govt_certifications",
            label: "Non-Federal government certifications",
            labelDescription:
              "In a spreadsheet export, list items will be comma-separated in a single cell.",
          },
        ],
      },
    ],
  },
  {
    name: "North American Industry Classification System (NAICS)",
    visible: true,
    columns: [
      {
        id: "naics_primary",
        name: "naics_primary",
        label: "Primary NAICS code",
      },
      {
        id: "naics_all_codes",
        name: "naics_all_codes",
        label: "All NAICS codes",
        labelDescription:
          "All NAICS codes will be listed comma-separated in a single cell.",
      },
      {
        id: "naics_small_codes",
        name: "naics_small_codes",
        label: "“Small” NAICS codes",
        labelDescription:
          "List all NAICS codes, comma-separated, that a business has a “small” designation for.",
      },
    ],
  },
  {
    name: "Service information",
    visible: true,
    columns: [
      {
        id: "special_equip_material",
        name: "special_equip_material",
        label: "Special equipment/materials",
      },
      {
        id: "construction_bonding_per_contract",
        name: "construction_bonding_per_contract",
        label: "Bonding level: Construction, per contract",
      },
      {
        id: "construction_bonding_aggregate",
        name: "construction_bonding_aggregate",
        label: "Bonding level: Construction, aggregate",
      },
      {
        id: "service_bonding_contract",
        name: "service_bonding_contract",
        label: "Bonding level: Service, per contract",
      },
      {
        id: "service_bonding_aggregate",
        name: "service_bonding_aggregate",
        label: "Bonding level: Service, aggregate",
      },
      {
        id: "keywords",
        name: "keywords",
        label: "Keywords",
        labelDescription:
          "Keywords will be listed comma-separated in a single cell.",
      },
      {
        id: "qas_standards",
        name: "qas_standards",
        label: "Quality assurance standards",
        labelDescription:
          "Quality assurance standards will be listed comma-separated in a single cell.",
      },
    ],
  },
];

export function downloadCSV(
  data: BusinessItem[],
  fileName: string,
  type: string
) {
  const csv = unparse(data);

  const blob = new Blob([csv], { type });

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = fileName;

  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);

  document.body.removeChild(a);
}

export function exportData(data: BusinessItem[], exportCols: Generic[]) {
  const date = moment(new Date()).format("YYYYMMDD");
  const fileName = `SBS_export_[${date}].csv`;

  const userRole = useAuthStore.getState().user?.role;
  const isPrivileged = userRole
    ? Permissions[userRole]?.privilegedSearch
    : false;

  // const mappedData = mapExportColumnsData(data, exportCols as any);
  const mappedData = mapExportColumnsDataExtended(
    data,
    exportCols as any,
    isPrivileged
  );

  downloadCSV(mappedData as any, fileName, "text/csv;");
}

const serviceInformationFields = [
  "cbona",
  "cbonc",
  "sbonc",
  "sbona",
  "special_equipment",
  "keywords",
];

const certificationStatuses = {
  certStatus: "cert_active",
  certDateStart: "start_date",
  certDateExit: "exit_date",
};

const certificationTypes = {
  "8a": "8(a) Certification",
  "8aJV": "8(a) Joint Venture",
  WOSB: "Women-Owned Small Business (WOSB)",
  EDWOSB: "Ecnonomically Disadvantaged Women-Owned Small Business (EDWOSB)",
  VOSB: "Veteran-Owned Small Business",
  SDVOSB: "Service-Disabled Veteran-Owned Small Business",
  HUBZone: "HUBZone Certification",
  VOSBJV: "Veteran-Owned Small Business Joint Venture",
  SDVOSBJV: "Service-Disabled Veteran-Owned Small Business Joint Venture",
};

const certStatusTypes = Object.keys(certificationStatuses).flatMap((status) =>
  Object.keys(certificationTypes).map((type) => `${status}_${type}`)
);

function mapExportColumnsData(data: BusinessItem[], exportCols: Generic[]) {
  const colNames = exportCols.reduce((acc, cur) => {
    if (cur.value === "businessName") {
      acc[cur.value] = "Business name";
    } else if (cur.value === "capabilitiesNarrative") {
      acc[cur.value] = "Capabilities narrative";
      acc["capabilitiesStatement"] = "Capabilities statement link";
    } else if (cur.value === "contact") {
      acc[cur.value] = "Contact person's name";
      acc["email"] = "Contact person's email";
    } else if (cur.value === "address") {
      if (!acc[cur.value]) {
        acc[cur.value] = {};
      }

      acc[cur.value]["line1"] = "Street address line 1";
      acc[cur.value]["line2"] = "Street address line 2";
      acc[cur.value]["city"] = "City";
      acc[cur.value]["state"] = "State";
      acc[cur.value]["zip"] = "Zip code";
    } else {
      acc[cur.value] = cur.label;
    }

    return acc;
  }, {});

  const exportData = data.map((item: BusinessItem) => {
    const colData = {};

    for (const key in colNames) {
      if (
        item[key] ||
        key === "capabilitiesStatement" ||
        serviceInformationFields.includes(key) ||
        certStatusTypes.includes(key)
      ) {
        if (key === "active_certifications") {
          colData[colNames[key]] = item[key]
            .filter((cert) => cert?.cert_active === "A")
            .map((cert) => refineCertificationTags(cert.name, false).name)
            .join(", ");
          // } else if (key === "address") {
          //   colData[colNames[key]["line1"]] = item[key].line1;
          //   colData[colNames[key]["line2"]] = item[key].line2;
          //   colData[colNames[key]["city"]] = item[key].city;
          //   colData[colNames[key]["state"]] = item[key].state;
          //   colData[colNames[key]["zip"]] = item[key].zip;
          // } else if (key === "current_principals") {
          //   colData[colNames[key]] = item[key].map(
          //     (principal) => `${principal.name}, ${principal.title}`
          //   );
        } else if (key === "capabilitiesStatement") {
          colData[colNames[key]] = getCapabilitiesNarrativeLink(item);
        } else if (serviceInformationFields.includes(key)) {
          colData[colNames[key]] = item.service_information[key];
        } else if (certStatusTypes.includes(key)) {
          const certInfo = key.split("_")[0];
          const certType = key.split("_")[1];
          let certStatus = [certificationStatuses[certInfo]];
          let value = item.active_certifications.find(
            (cert) => cert.name === certificationTypes[certType]
          )?.[certificationStatuses[certInfo]];
          if (value === "A") value = "Active";
          else if (value === "X") value = "Expired";
          else if (value === "I") value = "Expired";
          else if (value === "S") value = "Suspended";

          colData[colNames[key]] = value;
        } else {
          colData[colNames[key]] = item[key];
        }
      } else {
        colData[colNames[key]] = null;
      }
    }

    return colData;
  });

  return exportData;
}

// Reference from `ExportModal -> buildCSVData()` by Nick.
function mapExportColumnsDataExtended(
  data: BusinessItem[],
  exportCols: Generic[],
  isPrivileged: boolean,
) {
  const colNames = exportCols.reduce((acc, cur) => {
    if (cur.value === "legal_business_name") {
      acc[cur.value] = "Business name";
    } else if (cur.value === "capabilities_narrative") {
      acc[cur.value] = "Capabilities narrative";
      acc["capabilities_link"] = "Capabilities statement link";
    } else if (cur.value === "contact_person") {
      acc[cur.value] = "Contact person's name";
      acc["email"] = "Contact person's email";
    } else {
      acc[cur.value] = cur.label;
    }

    return acc;
  }, {});

  const exportData = data.map((item: BusinessItem) => {
    const colData = {};
    
    for (const key in colNames) {
      if (key === "last_update_date") {
        colData[colNames[key]] = formatUnixTimestamp(item[key]);
      } else if (key === "legal_business_name"){
        colData[colNames[key]] = `=HYPERLINK( "${window.location.origin}/profile/${item.uei}/${item.cage_code}", "${item[key]}" )`;
      } else if (key === "qas_standards") {
        colData[colNames[key]] = item.qas_standards.join(", ");
      } else if (key === "naics_all_codes") {
        colData[colNames[key]] = item.naics_all_codes.join(", ");
      } else if (key === "naics_small_codes") {
        colData[colNames[key]] = item.naics_small_codes.join(", ");
      } else if (key === "keywords") {
        colData[colNames[key]] = item.keywords.join(", ");
      } else if (key === "legal_structure") {
        colData[colNames[key]] = getLegalStructureName(item.legal_structure);
      } else if (key === "additional_website") {
        colData[colNames[key]] =
          item.additional_website?.indexOf("http") === -1
            ? `http://${item.additional_website}`
            : item.additional_website;
      } else if (key === "address_1") {
        const hideAddrInformation = !item.display_address;

        if (!hideAddrInformation || isPrivileged) {
          if (hideAddrInformation) {
            colData["Address line 1"] =
              (item.address_1 || "") +
              "\n" +
              "The business owner has hidden this information from public searches";
            colData["Address line 2"] =
              (item.address_2 || "") +
              "\n" +
              "The business owner has hidden this information from public searches";
            colData["City"] =
              (item.city || "") +
              "\n" +
              "The business owner has hidden this information from public searches";
            colData["State"] =
              (item.state || "") +
              "\n" +
              "The business owner has hidden this information from public searches";
            colData["Zipcode"] =
              (item.zipcode || "") +
              "\n" +
              "The business owner has hidden this information from public searches";
          } else {
            colData["Address line 1"] = item.address_1;
            colData["Address line 2"] = item.address_2;
            colData["City"] = item.city;
            colData["State"] = item.state;
            colData["Zipcode"] = item.zipcode;
          }
        } else {
          colData["Address line 1"] = "The business owner has hidden this information from public searches";
          colData["Address line 2"] = "The business owner has hidden this information from public searches";
          colData["City"] = "The business owner has hidden this information from public searches";
          colData["State"] = "The business owner has hidden this information from public searches";
          colData["Zipcode"] = "The business owner has hidden this information from public searches";
        }
      } else if (key === "capabilities_link") {
        // colData[colNames[key]] = getCapabilitiesNarrativeLink(item);
        colData[colNames[key]] = item?.capabilities_link;
      } else if (key === "certs") {
        colData[colNames[key]] = item.certs
          .filter((cert) => cert.active)
          .map((cert) => cert?.pending ? `${cert.name} Pending` : cert.name)
          .join(", ");
      } else if (key === "non_fed_govt_certifications") {
        colData[colNames[key]] = item.non_fed_govt_certifications
          ?.split(";")
          ?.join(", ");
      } else if (key === "meili_self_certifications") {
        colData[colNames[key]] = item.meili_self_certifications.join(", ");
      } else if(key.includes("certStatus")) {
          const status =
            item[key] === "Expired" ? "Previously certified" : item[key];
          colData[colNames[key]] = status;
      } else if (key === "email") {
        const email = item[key];
        const hideEmailInformation = !item.display_email;

        if (!hideEmailInformation || isPrivileged) {
          if (hideEmailInformation) {
            colData[colNames[key]] = email + "\n" + "The business owner has hidden this information from public searches";
          } else {
            colData[colNames[key]] = email;
          }
        } else {
          colData[colNames[key]] = "The business owner has hidden this information from public searches";
        }
      } else if (key === "phone") {
        const phone = item[key] || "";
        const hidePhoneInformation = !item.display_phone;

        if (!hidePhoneInformation || isPrivileged) {
          if (hidePhoneInformation) {
            colData[colNames[key]] = phone + "\n" + "The business owner has hidden this information from public searches";
          } else {
            colData[colNames[key]] = phone;
          }
        } else {
          colData[colNames[key]] = "The business owner has hidden this information from public searches";
        }
      } else if (key === "fax") {
        const fax = item[key] || "";
        const hideFaxInformation = !item.display_fax;

        if (!hideFaxInformation || isPrivileged) {
          if (hideFaxInformation) {
            colData[colNames[key]] = fax + "\n" + "The business owner has hidden this information from public searches";
          } else {
            colData[colNames[key]] = fax;
          }
        } else {
          colData[colNames[key]] = "The business owner has hidden this information from public searches";
        }
      } else {
        colData[colNames[key]] = item[key];
      }
    }

    return colData;
  });

  return exportData;
}